// Blocking per-user consent gate (fotoğraf #2). Lightweight KVKK Aydınlatma
// acknowledgment (required) + optional ETK / Ticari İleti opt-in — no signature
// ceremony. Records via POST /consents/{id}/acknowledge; on success the parent
// refetches and the gate clears. Links open the full policy text (reused
// TermsFullTextModal); the KVKK policy text is readable by any authenticated
// user server-side.

import { useState, type FormEvent } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useMutation } from '@tanstack/react-query'
import { DialogOverlay, DialogTitle } from '../../components/ui/dialog'
import { DialogPortal } from '../../components/ui/dialog.lib'
import { Button } from '../../components/ui/button'
import { cn } from '../../lib/utils'
import { acknowledgeConsent } from '../../api/consents'
import { useLocaleStore } from '../../store/locale'
import { uiToAgreementLocale } from '../../i18n/agreement-locales'
import { TermsFullTextModal } from './TermsFullTextModal'
import type { PendingConsent } from '../../lib/auth'

export function ConsentGate({
  consent,
  onDone,
}: {
  consent: PendingConsent
  onDone: () => void
}) {
  const locale = useLocaleStore((s) => s.locale)
  const tr = locale === 'tr'
  const [signLocale, setSignLocale] = useState(() => uiToAgreementLocale(locale))
  const [acknowledged, setAcknowledged] = useState(false)
  const [marketing, setMarketing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [kvkkOpen, setKvkkOpen] = useState(false)
  const [etkOpen, setEtkOpen] = useState(false)

  const mutation = useMutation({
    mutationFn: () =>
      acknowledgeConsent(consent.agreement_id, {
        acknowledged: true,
        marketing_opt_in: marketing,
        accepted_locale: signLocale,
      }),
    onSuccess: () => onDone(),
    onError: () =>
      setError(tr ? 'Kaydedilemedi, lütfen tekrar deneyin.' : 'Could not save, please retry.'),
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!acknowledged) return
    setError(null)
    mutation.mutate()
  }

  return (
    <DialogPrimitive.Root open>
      <DialogPortal>
        <DialogOverlay className="bg-slate-900/60" />
        <DialogPrimitive.Content
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-full max-w-[calc(100vw-1.5rem)] sm:max-w-lg',
            '-translate-x-1/2 -translate-y-1/2 gap-4 border border-slate-200 bg-white p-5 sm:p-6',
            'shadow-xl rounded-lg max-h-[calc(100dvh-1.5rem)] sm:max-h-[92vh] overflow-y-auto',
          )}
        >
          <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
            <div className="flex flex-col gap-1">
              <DialogTitle>{tr ? 'Hizmet Güncellemesi' : 'Service update'}</DialogTitle>
              <p className="text-sm text-slate-600">
                {tr
                  ? 'Yasal mevzuata uyum ve hizmet süreçlerimize ilişkin güncellemeler kapsamında ilgili metinlerimiz güncellenmiştir. Platformu kullanmaya devam edebilmek için lütfen aşağıdaki metinleri inceleyip gerekli onayları tamamlayınız.'
                  : 'As part of legal-compliance and service updates, the texts below have been updated. To keep using the platform, please review them and complete the required approvals.'}
              </p>
            </div>

            <fieldset className="flex flex-col gap-3">
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  aria-label={tr ? 'KVKK Aydınlatma Metni onayı' : 'KVKK notice acknowledgment'}
                  data-testid="consent-kvkk"
                />
                <span>
                  <button
                    type="button"
                    className="font-medium text-brand-700 underline"
                    onClick={() => setKvkkOpen(true)}
                  >
                    {consent.title}
                  </button>
                  {tr ? "'ni okudum ve bilgilendirildim." : ' — I have read and been informed.'}
                </span>
              </label>

              {consent.marketing && (
                <label className="flex items-start gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    aria-label={tr ? 'Ticari ileti izni (isteğe bağlı)' : 'Marketing opt-in (optional)'}
                    data-testid="consent-etk"
                  />
                  <span>
                    <span className="font-medium">{tr ? '(İsteğe bağlı) ' : '(Optional) '}</span>
                    {tr ? 'Kampanyalardan haberdar olmak için ' : 'To hear about campaigns, per the '}
                    <button
                      type="button"
                      className="font-medium text-brand-700 underline"
                      onClick={() => setEtkOpen(true)}
                    >
                      {consent.marketing.title}
                    </button>
                    {tr
                      ? ' kapsamında E-posta, SMS ve Telefon yoluyla tarafımla iletişime geçilmesine izin veriyorum.'
                      : ', I allow contact by e-mail, SMS and phone.'}
                  </span>
                </label>
              )}
            </fieldset>

            {error && (
              <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
            )}

            <Button
              type="submit"
              variant="primary"
              disabled={!acknowledged || mutation.isPending}
              data-testid="consent-submit"
            >
              {mutation.isPending
                ? tr
                  ? 'Kaydediliyor…'
                  : 'Saving…'
                : tr
                  ? 'Onayla ve Devam Et'
                  : 'Approve and continue'}
            </Button>
          </form>

          <TermsFullTextModal
            agreementId={consent.agreement_id}
            title={consent.title}
            open={kvkkOpen}
            onOpenChange={setKvkkOpen}
            locale={signLocale}
            onLocaleChange={setSignLocale}
          />
          {consent.marketing && (
            <TermsFullTextModal
              agreementId={consent.marketing.agreement_id}
              title={consent.marketing.title}
              open={etkOpen}
              onOpenChange={setEtkOpen}
              locale={signLocale}
              onLocaleChange={setSignLocale}
            />
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  )
}
